import api from '@/lib/api-client';
import { createFileRoute, Link } from '@tanstack/react-router'
import { Activity, Calendar, ChevronLeft, DollarSign, FileText, Mail, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { formatPreciseCurrency } from './billing-dashboard';

export const Route = createFileRoute('/billing/$clientId')({
  component: ClientDetailComponent,
})

function ClientDetailComponent() {
  const { clientId } = Route.useParams();

  const { data, isLoading } = useQuery({
    queryKey: ['client-billing', clientId],
    queryFn: () => api.billing.getClientDetail(clientId),
  });

  if (isLoading || !data) return <div className="p-8 text-gold-500 animate-pulse">Loading Client Analytics...</div>;

  const { summary } = data;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-200 p-8">
        <Link to="/billing/billing-dashboard" className="flex items-center text-sm text-slate-500 hover:text-gold-400 transition-colors mb-6 group">
            <ChevronLeft size={16} className="mr-1 group-hover:-translate-x-1 transition-transform" />
            Back to all clients
        </Link>

        <div className="flex items-center gap-4 mb-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center text-2xl font-bold text-primary-950 shadow-glow-gold">
              {data.client.name.charAt(0)}
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-white">{data.client.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                  <span className="flex items-center gap-1"><Mail size={14}/> {data.client.email || 'billing@creditNova.com'}</span>
                  <span className="flex items-center gap-1"><Calendar size={14}/> Since {new Date(data.client.createdAt).toLocaleDateString()}</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-bold uppercase">Active</span>
              </div>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <DetailStatCard title="DOCUMENTS" value={summary.documents} icon={<FileText size={16}/>} />
            <DetailStatCard 
                title="YOUR COST" 
                value={formatPreciseCurrency(summary.cost)} 
                valueClass="text-orange-400" 
                icon={<TrendingUp size={16}/>} 
            />
            <DetailStatCard 
                title="BILLED TO CLIENT" 
                value={formatPreciseCurrency(summary.revenue)} 
                valueClass="text-cyan-400" 
                icon={<DollarSign size={16}/>} 
            />
            <DetailStatCard 
                title="MARGIN" 
                value={formatPreciseCurrency(summary.margin)} 
                valueClass="text-green-400" 
                subtext={summary.revenue > 0 ? `${((summary.margin / summary.revenue) * 100).toFixed(1)}%` : "0%"}
                icon={<Activity size={16}/>} 
            />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1 bg-[#151921] rounded-xl border border-slate-800 p-6">
                <h3 className="text-[10px] font-bold text-slate-500 tracking-widest uppercase mb-6">COST BY PROVIDER</h3>
                <div className="space-y-6">
                {data.providerCosts.map((p: any) => (
                    <div key={p.model} className="flex justify-between items-center">
                    <div>
                        <p className="text-sm font-semibold text-white">
                        {p.provider === 'openai' ? 'OpenAI (GPT-5.2)' : p.provider === 'azure' ? 'Azure Document Intelligence' : 'Anthropic'} - {p.model}
                        </p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-tighter">
                        {p.calls} calls • {Number(p.units).toLocaleString()} {p.provider === 'openai' || p.provider === 'anthropic' ? 'tokens' : 'pages'}
                        </p>
                    </div>
                    <span className="text-sm font-mono font-bold text-orange-400">
                      {formatPreciseCurrency(p.cost)}
                    </span>
                    </div>
                ))}
                </div>
            </div>

            <div className="lg:col-span-2">
                <PipelineBarChart data={data.pipelineStats} />
            </div>
        </div>

        {/* Added the Trend Chart here */}
        <CostTrendChart data={data.dailyTrend} />

        <div className="bg-[#151921] rounded-xl border border-slate-800 overflow-hidden">
            <div className="p-5 border-b border-slate-800">
                <h3 className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">REVENUE BY DOCUMENT TYPE</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                <thead className="bg-[#1A1F29] text-slate-500 uppercase tracking-widest text-[9px]">
                    <tr>
                    <th className="px-6 py-4 font-medium">Document Type</th>
                    <th className="px-6 py-4 font-medium text-right">Count</th>
                    <th className="px-6 py-4 font-medium text-right">Revenue</th>
                    <th className="px-6 py-4 font-medium text-right">API Cost</th>
                    <th className="px-6 py-4 font-medium text-right">Margin</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                    {data.distribution?.map((item: any) => {
                        const revenue = Number(item.revenue);
                        const cost = Number(item.cost);
                        const margin = revenue - cost;
                        // Calculate margin percentage based on your 300x markup
                        // This should consistently hover around 99.7% if the markup is 300x
                        const marginPercent = revenue > 0 ? ((margin / revenue) * 100).toFixed(2) : "0.00";
                        
                        return (
                            <tr key={item.type} className="hover:bg-slate-800/30 transition-colors border-b border-slate-800/50 last:border-0">
                            <td className="px-6 py-4 font-medium text-slate-100">
                                <div className="flex flex-col">
                                <span>{item.type}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4 text-right text-slate-400 font-mono">{item.count}</td>
                            <td className="px-6 py-4 text-right text-emerald-400 font-mono font-bold">
                                {formatPreciseCurrency(revenue)}
                            </td>
                            <td className="px-6 py-4 text-right text-orange-300 font-mono text-[11px]">
                                {formatPreciseCurrency(cost)}
                            </td>
                            <td className="px-6 py-4 text-right font-mono">
                                <div className="flex flex-col items-end">
                                <span className="text-emerald-500 font-bold">{formatPreciseCurrency(margin)}</span>
                                <span className="text-[9px] px-1 bg-emerald-500/10 text-emerald-500 rounded border border-emerald-500/20">
                                    {marginPercent}%
                                </span>
                                </div>
                            </td>
                            </tr>
                        );
                    })}
                </tbody>
                </table>
            </div>
        </div>

    </div>
  );
}

// --- NEW SUB-COMPONENTS ADDED BELOW ---

function DetailStatCard({ title, value, icon, valueClass = "text-slate-100", subtext }: any) {
  return (
    <div className="bg-[#151921] p-5 rounded-xl border border-slate-800">
      <div className="flex justify-between items-center mb-3">
        <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">{title}</span>
        <span className="text-slate-600">{icon}</span>
      </div>
      <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
      {subtext && <div className="text-[10px] text-slate-500 mt-1">{subtext}</div>}
    </div>
  )
}

function PipelineBarChart({ data }: { data: any[] }) {
  return (
    <div className="bg-[#151921] rounded-xl border border-slate-800 p-6 h-full flex flex-col">
      <h3 className="text-[10px] font-bold text-slate-500 tracking-widest uppercase mb-6">COST BY PIPELINE STEP</h3>
      <div className="flex-grow">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E293B" />
            <XAxis 
              dataKey="category" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748B', fontSize: 10 }} 
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748B', fontSize: 10 }}
              tickFormatter={(value) => `$${Number(value).toFixed(2)}`}
            />
            <Tooltip 
                cursor={{ fill: '#ffffff05' }}
                contentStyle={{ backgroundColor: '#0B0E14', border: '1px solid #334155', borderRadius: '8px' }}
                formatter={(value: any) => [formatPreciseCurrency(value), 'Internal Cost']}
            />
            <Bar dataKey="cost" radius={[4, 4, 0, 0]} barSize={60}>
              {data.map((entry, index) => (
                // Adding unique colors for each category makes it look more professional
                <Cell 
                   key={`cell-${index}`} 
                   fill={getCategoryColor(entry.rawKey)} 
                   fillOpacity={0.8} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Optional helper for colors
function getCategoryColor(key: string) {
  const colors: Record<string, string> = {
    preprocessing: '#94a3b8', // Slate
    classification: '#fbbf24', // Amber
    extraction: '#22d3ee',    // Cyan
    analysis: '#818cf8',      // Indigo
    system: '#475569'         // Gray
  };
  return colors[key] || '#22d3ee';
}

function CostTrendChart({ data }: { data: any[] }) {
  return (
    <div className="bg-[#151921] p-6 rounded-xl border border-slate-800 h-80 mb-8">
      <h3 className="text-[10px] font-bold text-slate-500 tracking-widest uppercase mb-6">DAILY API COST TREND</h3>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00C2FF" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#00C2FF" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1E293B" />
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 10}} />
          <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 10}} tickFormatter={(v) => `$${v}`} />
          <Tooltip contentStyle={{backgroundColor: '#0B0E14', borderColor: '#334155'}} />
          <Area type="monotone" dataKey="cost" stroke="#00C2FF" fillOpacity={1} fill="url(#colorCost)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}